import { useState } from "react";
import { useSupabaseCallback, useSupabaseLoader } from "../../../server/supabase/hooks";
import { SupabaseAccountService } from "../server/supabase-account-service";
import { SupabaseContextService } from "../server/supabase-context-service";

export interface AccountContext {
  companyName: string | null;
  industry: string | null;
  companyDescription: string | null;
  companyValues: string | null;
  companyTeamReference: string | null;
}

type UseAccountContext = ReturnType<typeof useSupabaseLoader<AccountContext>> & {
  save: (context: Omit<AccountContext, "companyName">) => Promise<void>;
  saving: boolean;
};

const trimValue = (value: string | null): string | null => (value?.trim() ? value.trim() : null);

export const useAccountContext = (): UseAccountContext => {
  const [saving, setSaving] = useState(false);
  const loader = useSupabaseLoader(async ({ supabase, account_id }) => {
    const accountService = new SupabaseAccountService(supabase);
    const contextService = new SupabaseContextService(supabase);

    const [{ data: accountData }, data] = await Promise.all([
      accountService.get(account_id),
      contextService.getValues(account_id, [
        "industry",
        "company_description",
        "company_values",
        "company_team_reference",
      ]),
    ]);

    return {
      companyName: accountData?.business_name ?? null,
      industry: data.industry,
      companyDescription: data.company_description,
      companyValues: data.company_values,
      companyTeamReference: data.company_team_reference,
    };
  }, []);

  const { reload } = loader;

  const save = useSupabaseCallback<[Omit<AccountContext, "companyName">], void>(
    async ({ supabase }, context) => {
      setSaving(true);

      try {
        const contextService = new SupabaseContextService(supabase);
        const { error } = await contextService.setValues({
          industry: trimValue(context.industry),
          company_description: trimValue(context.companyDescription),
          company_values: trimValue(context.companyValues),
          company_team_reference: trimValue(context.companyTeamReference),
        });

        if (error) {
          throw new Error(`Failed to save company information: ${error.message}`);
        }

        reload();
      } finally {
        setSaving(false);
      }
    },
    [reload],
  );

  return { ...loader, save, saving };
};
